import { ref } from "vue";
import type { ContainerSlot } from "./useContainerStream";
import { API_BASE } from "@/config";

export function useContainerSearch() {
  const query = ref("");
  const results = ref<ContainerSlot[]>([]);
  const isSearching = ref(false);
  const searchError = ref<string | null>(null);

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  async function search() {
    // Normalize: collapse newlines/spaces around commas into a clean comma list
    const q = query.value
      .trim()
      .replace(/[\r\n]+/g, ",") // newline
      .replace(/\s*,\s*/g, ",") // comma + space between
      .replace(/,+/g, ",") // multiple commas
      .replace(/^,|,$/g, "");

    const isListMode = q.includes(",");

    if (q.length === 0 || (!isListMode && q.length < 2)) {
      results.value = [];
      return;
    }

    isSearching.value = true;
    searchError.value = null;

    try {
      const res = await fetch(
        `${API_BASE}/api/tos/containers/search?q=${encodeURIComponent(q)}`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      results.value = await res.json();
    } catch (e) {
      searchError.value = e instanceof Error ? e.message : "Lỗi kết nối TOS";
      results.value = [];
    } finally {
      isSearching.value = false;
    }
  }

  /** Call this on input — debounces 350ms */
  function debouncedSearch() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(search, 350);
  }

  function clearSearch() {
    if (debounceTimer) clearTimeout(debounceTimer);
    query.value = "";
    results.value = [];
    searchError.value = null;
  }

  return {
    query,
    results,
    isSearching,
    searchError,
    debouncedSearch,
    search,
    clearSearch,
  };
}
